// Zod contracts for the New Client Offer owner side: enum schemas, settings + messages (write/read).
// Limits are re-enforced by DB CHECKs (migration `new_client_offer_module`), so the two change together.
import { z } from 'zod';
import {
  NEW_CLIENT_OFFER_STATUSES,
  OFFER_CLAIM_EVENT_TYPES,
  OFFER_CLAIM_STATUSES,
  OFFER_DISCOUNT_TYPES,
  OFFER_MESSAGE_STEPS,
  OFFER_NOT_ELIGIBLE_REASONS,
  OFFER_PLACEMENTS,
  OFFER_REDEEM_MODES,
  OFFER_REDEMPTION_METHODS,
  OFFER_SIGNUP_CHANNELS,
  PUBLIC_OFFER_PLACEMENTS,
} from './new-client-offer-enums';
import { OFFER_MESSAGE_STEP_META } from './new-client-offer-default-messages';
import { isOnlyOfferArrivalLine } from './offer-redeem-way-phrases';

export const NEW_CLIENT_OFFER_LIMITS = {
  headlineMax: 60,
  finePrintMax: 300,
  signupNoteMax: 200,
  validityDaysMin: 1,
  validityDaysMax: 90,
  validityDaysDefault: 14,
  percentOffMin: 1,
  percentOffMax: 100,
  amountOffCentsMin: 100,
  amountOffCentsMax: 50_000,
  bioSheetDelaySecondsMin: 0,
  bioSheetDelaySecondsMax: 30,
  bioSheetDelaySecondsDefault: 3,
  messageBodyMax: 640,
  messageDelayDaysMax: 90,
} as const;
const L = NEW_CLIENT_OFFER_LIMITS;

export const newClientOfferStatusSchema = z.enum(NEW_CLIENT_OFFER_STATUSES);
export const offerDiscountTypeSchema = z.enum(OFFER_DISCOUNT_TYPES);
export const offerPlacementSchema = z.enum(OFFER_PLACEMENTS);
export const publicOfferPlacementSchema = z.enum(PUBLIC_OFFER_PLACEMENTS);
export const offerSignupChannelSchema = z.enum(OFFER_SIGNUP_CHANNELS);
export const offerClaimStatusSchema = z.enum(OFFER_CLAIM_STATUSES);
export const offerNotEligibleReasonSchema = z.enum(OFFER_NOT_ELIGIBLE_REASONS);
export const offerRedemptionMethodSchema = z.enum(OFFER_REDEMPTION_METHODS);
export const offerRedeemModeSchema = z.enum(OFFER_REDEEM_MODES);

/**
 * Guest pages' `counterStand`: every active location of the salon has an active Hayven Tap stand at the front desk, so
 * with `COUNTER_QR` the page says "tap your phone on the stand (or scan its code)" instead of "scan the QR code". Not a
 * redeem mode of its own. Defaults to false so an older API response keeps the counter sign copy.
 */
export const counterStandFieldSchema = z.boolean().default(false);
export const offerClaimEventTypeSchema = z.enum(OFFER_CLAIM_EVENT_TYPES);
export const offerMessageStepSchema = z.enum(OFFER_MESSAGE_STEPS);

export const offerTimestampSchema = z.string().datetime({ offset: true });

/** Exactly one discount value, matching the type (the DB CHECK `new_client_offers_discount_check`). */
export const offerDiscountSchema = z
  .object({
    discountType: offerDiscountTypeSchema,
    percentOff: z.number().int().min(L.percentOffMin).max(L.percentOffMax).nullable(),
    amountOffCents: z.number().int().min(L.amountOffCentsMin).max(L.amountOffCentsMax).nullable(),
  })
  .superRefine((value, ctx) => {
    const percent = value.discountType === 'PERCENT';
    const [required, forbidden] = percent
      ? (['percentOff', 'amountOffCents'] as const)
      : (['amountOffCents', 'percentOff'] as const);
    if (value[required] === null)
      ctx.addIssue({ code: 'custom', path: [required], message: percent ? 'Enter a percent' : 'Enter an amount' });
    if (value[forbidden] !== null)
      ctx.addIssue({ code: 'custom', path: [forbidden], message: 'Only one discount value can be set' });
  });

/** Editable offer settings (everything but the discount, which is validated as a group). */
const newClientOfferSettingsFieldsSchema = z.object({
  headline: z.string().trim().min(1).max(L.headlineMax),
  finePrint: z.string().trim().max(L.finePrintMax),
  /** Optional on save: an editor that predates it (an older cached web build) leaves the stored note unchanged. */
  signupNote: z.string().trim().max(L.signupNoteMax).optional(),
  validityDays: z.number().int().min(L.validityDaysMin).max(L.validityDaysMax),
  bioBannerEnabled: z.boolean(),
  bioSheetEnabled: z.boolean(),
  bioSheetDelaySeconds: z.number().int().min(L.bioSheetDelaySecondsMin).max(L.bioSheetDelaySecondsMax),
  bookingBannerEnabled: z.boolean(),
  /**
   * The embed code on the salon's own website shows the form only while this is on (opt-in). Optional on save:
   * an editor that predates it (an older cached web build) leaves the stored value unchanged.
   */
  websiteEmbedEnabled: z.boolean().optional(),
  metaVisitEventEnabled: z.boolean(),
});

/** Whole-form save. `baseVersion` is the version the editor loaded; a mismatch is a 409. */
export const newClientOfferSettingsWriteSchema = z.object({
  baseVersion: z.number().int().min(0),
  discount: offerDiscountSchema,
  settings: newClientOfferSettingsFieldsSchema,
});
export type NewClientOfferSettingsWrite = z.infer<typeof newClientOfferSettingsWriteSchema>;

/**
 * One journey step. Timing must match the step: an IMMEDIATE step (pass, missed visit, welcome back)
 * carries no days/hour; a scheduled step (reminders, last chance) needs both.
 */
export const offerMessageConfigSchema = z
  .object({
    step: offerMessageStepSchema,
    enabled: z.boolean(),
    bodyText: z.string().trim().min(1).max(L.messageBodyMax).refine((body) => !isOnlyOfferArrivalLine(body), {
      message: 'Add some words around the arrival line.',
    }),
    delayDays: z.number().int().min(0).max(L.messageDelayDaysMax).nullable(),
    sendHour: z.number().int().min(0).max(23).nullable(),
  })
  .superRefine((value, ctx) => {
    const immediate = OFFER_MESSAGE_STEP_META[value.step].timing === 'IMMEDIATE';
    for (const field of ['delayDays', 'sendHour'] as const) {
      if (immediate && value[field] !== null)
        ctx.addIssue({ code: 'custom', path: [field], message: 'This message sends right away' });
      if (!immediate && value[field] === null)
        ctx.addIssue({ code: 'custom', path: [field], message: 'Choose when this message sends' });
    }
  });
export type OfferMessageConfig = z.infer<typeof offerMessageConfigSchema>;

export const newClientOfferMessagesWriteSchema = z.object({
  baseVersion: z.number().int().min(0),
  messages: z
    .array(offerMessageConfigSchema)
    .max(OFFER_MESSAGE_STEPS.length)
    .refine((rows) => new Set(rows.map((row) => row.step)).size === rows.length, 'Each step can appear once'),
});

export const newClientOfferReadSchema = newClientOfferSettingsFieldsSchema.extend({
  signupNote: z.string(),
  websiteEmbedEnabled: z.boolean(),
  id: z.string(),
  status: newClientOfferStatusSchema,
  discountType: offerDiscountTypeSchema,
  percentOff: z.number().int().nullable(),
  amountOffCents: z.number().int().nullable(),
  version: z.number().int(),
  liveAt: offerTimestampSchema.nullable(),
  updatedAt: offerTimestampSchema,
  messages: z.array(offerMessageConfigSchema),
});
export type NewClientOfferRead = z.infer<typeof newClientOfferReadSchema>;
